/**
 * MissingFieldBadge · W126.1 · 2026-05-26
 *
 * Tech debt do W125.1 — quando o PM finaliza o wizard sem preencher
 * `address` ou `clientName`, o backend persiste o fallback "A definir"
 * (ver `apps/api/src/services/project-wizard.service.ts:215`). Esse
 * sentinel literal vaza pra invoices/POs/relatórios se ninguém volta
 * pra editar a obra.
 *
 * Esta badge é o sinal visual leve de "essa obra ainda precisa de você"
 * — substitui textos crus tipo "A definir" por um chip warn-tone +
 * deep link pra corrigir.
 *
 * Detecção: o caller (ProjectsPage card / ProjectHero) compara o valor
 * persistido com o sentinel literal `'A definir'` (lowercase também,
 * defensivo) ou null/empty. Lista `fields` é renderizada agrupada
 * separada por " · ".
 *
 * Deep link: navega pra `/projects/:id?focus=<field>` (visao-geral).
 * Quando o W125+ adicionar form inline de edição, basta o focus query
 * abrir o input correto sem mudar este componente.
 *
 * Termo PM-friendly: "Falta endereço" / "Falta cliente" / "Falta tipo"
 * — segue glossário (`apps/web/src/lib/term-glossary.ts`) e
 * `OPERATING-MANUAL.md` (sem `payload`, `tier`, "A definir" raw).
 */

import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

export type MissingField = 'address' | 'clientName' | 'tipoObra';

interface Props {
  fields: MissingField[];
  projectId: string;
  /** Tamanho compacto pra card list (sm) vs hero (md). Default 'sm'. */
  size?: 'sm' | 'md';
  className?: string;
  'data-testid'?: string;
}

const LABELS_PTBR: Record<MissingField, string> = {
  address: 'Falta endereço',
  clientName: 'Falta cliente',
  tipoObra: 'Falta tipo',
};

const LABELS_EN: Record<MissingField, string> = {
  address: 'Missing address',
  clientName: 'Missing client',
  tipoObra: 'Missing type',
};

/**
 * Detecta quais campos estão faltando num projeto. Centralizado aqui
 * pra ProjectsPage card e ProjectHero usarem a mesma regra (evita drift
 * entre superfícies). O sentinel "A definir" é o literal escrito por
 * `project-wizard.service.ts`; comparado case-insensitive por defesa.
 */
export function detectMissingFields(p: {
  address?: string | null;
  clientName?: string | null;
  tipoObra?: string | null;
}): MissingField[] {
  const missing: MissingField[] = [];
  const isBlank = (v?: string | null) => {
    if (!v) return true;
    const t = v.trim().toLowerCase();
    return t === '' || t === 'a definir';
  };
  if (isBlank(p.address)) missing.push('address');
  if (isBlank(p.clientName)) missing.push('clientName');
  if (!p.tipoObra) missing.push('tipoObra');
  return missing;
}

export function MissingFieldBadge({
  fields,
  projectId,
  size = 'sm',
  className,
  ...rest
}: Props) {
  const navigate = useNavigate();
  const { locale } = useT();
  const isEn = locale === 'en';
  if (!fields.length) return null;
  const labels = isEn ? LABELS_EN : LABELS_PTBR;
  const text = fields.map((f) => labels[f]).join(' · ');
  const sizeClass =
    size === 'md'
      ? 'px-2.5 py-1 text-xs'
      : 'px-2 py-0.5 text-[11px]';
  // W126.1 → corrigido 2026-06-06 (SETUP-01): antes apontava pra
  // `/projects/:id?focus=<field>`, mas o index dessa rota faz
  // `<Navigate to="hoje" replace />` (router.tsx) — o redirect DESCARTA o
  // query param e o /hoje não tem editor de tipo. O "corrigir" não fazia
  // nada útil → nag permanente (CLAUDE.md padrão #2: nunca gerar link que
  // não faz nada). Agora aponta pra /visao-geral, que tem o TipoObraCard
  // editável e consome `?focus=` via useEffect (scroll + abre o editor).
  const href = `/projects/${projectId}/visao-geral?focus=${fields[0]}`;
  // Atenção: o <ProjectCard> envolve tudo num <Link>. Anchor dentro de
  // anchor é HTML inválido (warning validateDOMNesting do React). Por
  // isso usamos <button> + navigate programático em vez de <Link>.
  // Mantemos o atributo `href` na nossa estrutura via data attribute
  // (testes leem `href`) e expomos via `data-href` pra QA. O click
  // stopPropagation evita o pai <Link> capturar e navegar pro detail.
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full whitespace-nowrap',
        'bg-warn/10 text-warn border border-warn/25',
        sizeClass,
        className,
      )}
      data-testid={rest['data-testid'] ?? 'missing-field-badge'}
    >
      <AlertCircleIcon size={size === 'md' ? 14 : 12} />
      <span>{text}</span>
      <button
        type="button"
        data-testid="missing-field-fix-link"
        data-href={href}
        aria-label={isEn ? `Fix ${text}` : `Corrigir ${text}`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          navigate(href);
        }}
        className="ml-0.5 underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-warn/50 rounded bg-transparent border-0 p-0 text-inherit cursor-pointer"
      >
        {isEn ? 'fix' : 'corrigir'}
      </button>
    </span>
  );
}

function AlertCircleIcon({ size = 12 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default MissingFieldBadge;
